import type { AxiosInstance } from "axios";
import HttpFactory from "~/repository/factory";
import type { SchoolPage } from "~/types/schoolManagement";
import type {
  SchoolFormDraft,
  SchoolFormRecord,
  SchoolFormSummary,
  SchoolFormRevision,
  SchoolFormSubmission,
  SchoolFormSubmissionSummary,
} from "~/types/schoolForms";

export interface SchoolFormQuery {
  search?: string;
  status?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
  revision_number?: number | null;
  converted?: boolean | null;
  submitted_from?: string;
  submitted_to?: string;
}

const root = "/api/organizations/current/forms/";
const path = (id: string) => `${root}${encodeURIComponent(id)}/`;
const queryParams = (query: SchoolFormQuery) =>
  Object.fromEntries(
    Object.entries(query).filter(
      ([, value]) => value !== "" && value !== undefined && value !== null,
    ),
  );

export default class SchoolFormsModule extends HttpFactory {
  constructor(axios: AxiosInstance) {
    super(axios, "school");
  }

  list(query: SchoolFormQuery = {}) {
    return this.get<SchoolPage<SchoolFormSummary>>(root, queryParams(query));
  }

  create(template: "blank" | "english_interest") {
    return this.post<SchoolFormRecord>(root, { template });
  }

  getForm(id: string) {
    return this.get<SchoolFormRecord>(path(id));
  }

  save(id: string, version: number, draft: SchoolFormDraft) {
    return this.patch<SchoolFormRecord>(path(id), {
      expected_version: version,
      draft,
    });
  }

  publish(id: string, version: number) {
    return this.post<SchoolFormRecord>(`${path(id)}publish/`, {
      expected_version: version,
    });
  }

  receiving(id: string, version: number, value: boolean) {
    return this.post<SchoolFormRecord>(`${path(id)}receiving/`, {
      expected_version: version,
      is_receiving: value,
    });
  }

  archive(id: string, version: number) {
    return this.post<SchoolFormRecord>(`${path(id)}archive/`, {
      expected_version: version,
    });
  }

  restore(id: string, version: number) {
    return this.post<SchoolFormRecord>(`${path(id)}restore/`, {
      expected_version: version,
    });
  }

  duplicate(id: string, version: number) {
    return this.post<SchoolFormRecord>(`${path(id)}duplicate/`, {
      expected_version: version,
    });
  }

  revisions(id: string) {
    return this.get<SchoolFormRevision[]>(`${path(id)}revisions/`);
  }

  submissions(id: string, query: SchoolFormQuery = {}) {
    return this.get<SchoolPage<SchoolFormSubmissionSummary>>(
      `${path(id)}submissions/`,
      queryParams(query),
    );
  }

  interestSubmissions(interestId: number, page = 1) {
    return this.get<
      SchoolPage<SchoolFormSubmissionSummary & { form_title: string }>
    >(`/api/organizations/current/interests/${interestId}/form-submissions/`, {
      page,
      page_size: 25,
    });
  }

  submission(id: string, submissionId: string) {
    return this.get<SchoolFormSubmission>(
      `${path(id)}submissions/${encodeURIComponent(submissionId)}/`,
    );
  }

  convert(
    id: string,
    submissionId: string,
    contact: { full_name: string; email: string; phone: string },
  ) {
    return this.post<SchoolFormSubmission>(
      `${path(id)}submissions/${encodeURIComponent(submissionId)}/convert/`,
      contact,
    );
  }

  exportSubmissions(id: string, query: SchoolFormQuery = {}) {
    const { page: _page, page_size: _pageSize, ...filters } = query;
    return this.get<Blob>(`${path(id)}export/`, queryParams(filters), {
      responseType: "blob",
    });
  }
}
